import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { FileText, Edit, Trash2, Loader2, Plus } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

const PLATFORM_EMOJI = {
    instagram: '📸',
    facebook: '👤',
    twitter: '🐦',
    linkedin: '💼',
    tiktok: '🎵',
    pinterest: '📌',
    youtube: '🎬',
};

export default function Drafts() {
    const { api } = useAuth();
    const navigate = useNavigate();
    const [drafts, setDrafts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [draftToDelete, setDraftToDelete] = useState(null);

    const fetchDrafts = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.get('/posts?status=draft');
            setDrafts(res.data);
        } catch (err) {
            toast.error('Failed to load drafts');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => { fetchDrafts(); }, [fetchDrafts]);

    const handleDelete = async () => {
        if (!draftToDelete) return;
        try {
            await api.delete(`/posts/${draftToDelete.id}`);
            toast.success('Draft deleted');
            setDeleteDialogOpen(false);
            setDraftToDelete(null);
            fetchDrafts();
        } catch (err) {
            toast.error('Failed to delete draft');
        }
    };

    return (
        <div className="space-y-6 animate-fade-in" data-testid="drafts-page">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h1 className="text-3xl font-bold flex items-center gap-2">
                        <FileText className="h-7 w-7" />
                        Drafts
                    </h1>
                    <p className="text-muted-foreground mt-1">
                        Posts saved but not yet scheduled or published
                    </p>
                </div>
                <Button onClick={() => navigate('/compose')} className="gradient-bg" data-testid="new-draft-btn">
                    <Plus className="mr-2 h-4 w-4" />
                    New post
                </Button>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-[300px]">
                    <Loader2 className="h-8 w-8 animate-spin text-primary" />
                </div>
            ) : drafts.length === 0 ? (
                <Card>
                    <CardContent className="flex flex-col items-center justify-center py-16 text-center">
                        <FileText className="h-12 w-12 text-muted-foreground mb-3 opacity-50" />
                        <p className="text-muted-foreground mb-4">No drafts yet.</p>
                        <Button onClick={() => navigate('/compose')} variant="outline">
                            Create your first draft
                        </Button>
                    </CardContent>
                </Card>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {drafts.map(draft => (
                        <Card key={draft.id} className="hover:border-primary/50 transition-colors" data-testid={`draft-${draft.id}`}>
                            <CardHeader className="pb-3">
                                <div className="flex items-start justify-between gap-2">
                                    <CardTitle className="text-base font-medium line-clamp-2 flex-1">
                                        {draft.caption?.slice(0, 80) || 'No caption'}
                                        {draft.caption?.length > 80 ? '...' : ''}
                                    </CardTitle>
                                    <Badge variant="secondary" className="flex-shrink-0">draft</Badge>
                                </div>
                            </CardHeader>
                            <CardContent className="space-y-3">
                                <div className="flex flex-wrap gap-1">
                                    {(draft.platforms || []).map(p => (
                                        <span key={p} className="text-lg" title={p}>{PLATFORM_EMOJI[p] || p}</span>
                                    ))}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    {(draft.media_ids?.length || 0)} media · {(draft.social_account_ids?.length || 0)} account(s)
                                </div>
                                <div className="text-xs text-muted-foreground">
                                    Saved {draft.updated_at ? format(new Date(draft.updated_at), 'MMM d, h:mm a') : '—'}
                                </div>
                                <div className="flex gap-2 pt-2">
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        className="flex-1"
                                        onClick={() => navigate(`/compose?edit=${draft.id}`)}
                                        data-testid={`edit-${draft.id}`}
                                    >
                                        <Edit className="mr-2 h-3 w-3" />
                                        Edit
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => { setDraftToDelete(draft); setDeleteDialogOpen(true); }}
                                        data-testid={`delete-${draft.id}`}
                                    >
                                        <Trash2 className="h-3 w-3 text-destructive" />
                                    </Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
                <DialogContent>
                    <DialogHeader>
                        <DialogTitle>Delete draft?</DialogTitle>
                        <DialogDescription>
                            This action cannot be undone.
                        </DialogDescription>
                    </DialogHeader>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Cancel</Button>
                        <Button variant="destructive" onClick={handleDelete}>Delete</Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
